/**
 * Player death FX — the dramatic burst when the ball is hit. Bigger and more violent
 * than a bomb blast: a white flash, a coloured shockwave ring, a large radial spray
 * of bright embers, a shower of skin-coloured debris chunks that arc + fall, and a
 * strong camera shake. The ball is hidden the instant this fires (the explosion
 * replaces it) and the in-flight attacks keep animating behind it before the
 * game-over screen appears.
 *
 * Purely decorative + pooled (lazy emitters + two reused images); one instance per
 * scene. The colours come from the player's skin so it reads as THE BALL bursting.
 */
import Phaser from 'phaser';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

/** Above the player (10/11) and all hazard FX so the burst dominates. */
const FX_DEPTH = 13;

const SPARK_COUNT = 84;
const DEBRIS_COUNT = 18;
const RING_MS = 540;
const FLASH_MS = 170;
const SHOCK_TEX = 128;
const FLASH_TEX = 32;

export class PlayerDeathFx {
  private readonly scene: Phaser.Scene;
  private sparks?: Phaser.GameObjects.Particles.ParticleEmitter;
  private debris?: Phaser.GameObjects.Particles.ParticleEmitter;
  private flash?: Phaser.GameObjects.Image;
  private ring?: Phaser.GameObjects.Image;
  private flashTween?: Phaser.Tweens.Tween;
  private ringTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Detonate the player at `(x,y)` using the skin's `body`/`highlight`/`rim` colours.
   * `r` is the ball radius; the burst is scaled well past it for drama.
   */
  blast(x: number, y: number, r: number, body: number, highlight: number, rim: number): void {
    this.ensureObjects();

    // White flash.
    const flash = this.flash!;
    this.flashTween?.stop();
    const peak = (r * 3.4 * 2) / FLASH_TEX;
    flash
      .setPosition(x, y)
      .setActive(true)
      .setVisible(true)
      .setAlpha(1)
      .setTint(0xffffff)
      .setScale(peak * 0.35);
    this.flashTween = this.scene.tweens.add({
      targets: flash,
      scale: peak,
      alpha: 0,
      duration: FLASH_MS,
      ease: 'quad.out',
      onComplete: () => flash.setVisible(false).setActive(false),
    });

    // Coloured shockwave ring.
    const ring = this.ring!;
    this.ringTween?.stop();
    const startScale = (r * 0.8 * 2) / SHOCK_TEX;
    const endScale = (r * 4.2 * 2) / SHOCK_TEX;
    ring
      .setPosition(x, y)
      .setActive(true)
      .setVisible(true)
      .setAlpha(0.95)
      .setTint(highlight)
      .setScale(startScale);
    this.ringTween = this.scene.tweens.add({
      targets: ring,
      scale: endScale,
      alpha: 0,
      duration: RING_MS,
      ease: 'cubic.out',
      onComplete: () => ring.setVisible(false).setActive(false),
    });

    // Bright radial ember spray (rises slightly like a fireball of the ball's colour).
    const sparks = this.sparks!;
    const reach = r * 4.5;
    const speed = (reach / 0.6) * 1.2;
    sparks.updateConfig({
      speed: { min: speed * 0.35, max: speed },
      color: [0xffffff, highlight, body, HAZARD_COLORS.blastDeep],
    });
    sparks.explode(SPARK_COUNT, x, y);

    // Skin-coloured debris chunks that arc out + fall.
    const debris = this.debris!;
    debris.updateConfig({ speed: { min: r * 3, max: r * 9 }, tint: [body, highlight, rim] });
    debris.explode(DEBRIS_COUNT, x, y);

    // A strong slam.
    this.scene.cameras.main.shake(320, 0.014);
  }

  reset(): void {
    this.flashTween?.stop();
    this.ringTween?.stop();
    this.flashTween = undefined;
    this.ringTween = undefined;
    this.flash?.setVisible(false).setActive(false);
    this.ring?.setVisible(false).setActive(false);
    this.sparks?.killAll();
    this.debris?.killAll();
  }

  destroy(): void {
    this.flashTween?.stop();
    this.ringTween?.stop();
    this.sparks?.destroy();
    this.debris?.destroy();
    this.flash?.destroy();
    this.ring?.destroy();
    this.sparks = undefined;
    this.debris = undefined;
    this.flash = undefined;
    this.ring = undefined;
  }

  /* --------------------------------------------------------------------- */

  private ensureObjects(): void {
    if (!this.sparks) {
      this.sparks = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          frequency: -1,
          lifespan: { min: 380, max: 780 },
          speed: { min: 80, max: 360 },
          angle: { min: 0, max: 360 },
          gravityY: -120,
          scale: { start: 1.1, end: 0, ease: 'quad.out' },
          alpha: { start: 1, end: 0, ease: 'quad.in' },
          rotate: { min: 0, max: 360 },
          color: [0xffffff, HAZARD_COLORS.turretCyan, HAZARD_COLORS.turretBody, HAZARD_COLORS.blastDeep],
          colorEase: 'quad.out',
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        })
        .setDepth(FX_DEPTH);
      this.sparks.reserve(SPARK_COUNT * 2);
    }
    if (!this.debris) {
      this.debris = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          frequency: -1,
          lifespan: { min: 520, max: 980 },
          speed: { min: 100, max: 420 },
          angle: { min: 0, max: 360 },
          gravityY: 640, // chunks arc + fall
          scale: { start: 0.5, end: 0, ease: 'quad.out' },
          alpha: { start: 1, end: 0, ease: 'quad.in' },
          rotate: { min: 0, max: 360 },
          emitting: false,
        })
        .setDepth(FX_DEPTH - 1);
      this.debris.reserve(DEBRIS_COUNT * 2);
    }
    if (!this.flash) {
      this.flash = this.scene.add
        .image(0, 0, HAZARD_TEX.spark)
        .setDepth(FX_DEPTH + 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false)
        .setActive(false);
    }
    if (!this.ring) {
      this.ring = this.scene.add
        .image(0, 0, HAZARD_TEX.shock)
        .setDepth(FX_DEPTH)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false)
        .setActive(false);
    }
  }
}
